import {
	faBuilding as fasBuilding,
	faCalendarDays as fasCalendarDays,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { AbstractStep } from "@/component/assets/AbstractLeadForm/Steps/AbstractStep";
import { currentYear } from "@/shared/lib/current-year";
import { ButtonLegacy, FormCurrency, FormDecidable, FormNumber } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";
import {
	MAX_LOAN_AMOUNT,
	MAX_LOAN_DURATION,
	MAX_MONTHLY_INCOME,
	MIN_LOAN_AMOUNT,
	MIN_LOAN_DURATION,
	MIN_MONTHLY_INCOME,
	MIN_REAL_ESTATE_VALUE,
} from "../constants";
import type { FormFields } from "../types";

export interface StepFields {
	loanAmount: number;
	maturityYears: number;
	monthlyIncome: number;
	realEstateValue: number;
	qualifiesForHigherLtv: boolean;
}

export class Step1 extends AbstractStep<StepFields> {
	protected initValues(): Partial<StepFields> {
		return {
			loanAmount: undefined,
			maturityYears: undefined,
			monthlyIncome: undefined,
			realEstateValue: undefined,
			qualifiesForHigherLtv: true,
		};
	}

	protected renderContent(_values: StepFields, _form: FormApi<StepFields>): ReactNode {
		const disabled = this.isDisabled();

		return (
			<>
				<Card.Header className="bg-secondary-gradient text-white text-center">
					<div className="px-3 py-1 display-5">
						<strong>
							Tudd meg, hogy {currentYear()}-től mennyivel jársz jobban
							<br /> az új CSOK Plusszal!
						</strong>
					</div>
				</Card.Header>
				<Card.Body className="d-flex mt-2 flex-column">
					<FormCurrency
						label="Ingatlan vételára"
						name="realEstateValue"
						validate={this.validRealEstateValue}
						prepend={<FontAwesomeIcon icon={fasBuilding} />}
						disabled={disabled}
						required
					/>
					<FormCurrency
						label="Hitelösszeg (2 500 000 - 50 000 000 Ft)"
						name="loanAmount"
						validate={this.validLoanValue}
						disabled={disabled}
						required
					/>
					<FormNumber
						label="Futamidő (10 - 25 év)"
						name="maturityYears"
						validate={this.validmaturityYears}
						prepend={<FontAwesomeIcon icon={fasCalendarDays} />}
						disabled={disabled}
						required
					/>
					<FormCurrency
						label="Család havi jövedelme"
						name="monthlyIncome"
						validate={this.validMonthlyIncome}
						disabled={disabled}
						required
					/>
					<FormDecidable
						label="Megfelelek a 10% önerő előírásainak"
						className="mt-2"
						name="qualifiesForHigherLtv"
						disabled={disabled}
						infoTooltip="Az igénylők egyike sem rendelkezhetett lakásban 50%-ot elérő tulajdoni hányaddal (kivéve olyan lakástulajdonnal, ami haszonélvezeti joggal terhelt)."
						required
					/>
				</Card.Body>
				<Card.Footer className="d-flex mb-1">
					<ButtonLegacy type="submit" className="mb-1 font-weight-bold" block>
						Tovább
					</ButtonLegacy>
				</Card.Footer>
			</>
		);
	}

	private validLoanValue(value: number, values: FormFields): string {
		const { qualifiesForHigherLtv, realEstateValue } = values;

		if (qualifiesForHigherLtv) {
			if (value > realEstateValue * 0.9) {
				return `A hitelösszeg nem lehet nagyobb az ingatlan értékének 90%-ánál (${formatText(realEstateValue * 0.9, "ft")})!`;
			}
		} else if (value > realEstateValue * 0.8) {
			return `A hitelösszeg nem lehet nagyobb az ingatlan értékének 80%-ánál (${formatText(realEstateValue * 0.8, "ft")})!`;
		}

		if (value < MIN_LOAN_AMOUNT) {
			return `Nem lehet kisebb a hitelösszeg mint ${formatText(MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (value > MAX_LOAN_AMOUNT) {
			return `Nem lehet nagyobb a hitelösszeg mint ${formatText(MAX_LOAN_AMOUNT, "ft")}!`;
		}
	}

	private validmaturityYears(value: number) {
		if (value < MIN_LOAN_DURATION) {
			return `A futamidő nem lehet rövidebb ${MIN_LOAN_DURATION} évnél!`;
		}

		if (value > MAX_LOAN_DURATION) {
			return `A futamidő nem lehet hosszabb ${MAX_LOAN_DURATION} évnél!`;
		}
	}

	private validMonthlyIncome(value: number): string {
		if (value < MIN_MONTHLY_INCOME) {
			return `A jövedelem nem lehet kevesebb mint ${formatText(MIN_MONTHLY_INCOME, "ft")}!`;
		}

		if (value > MAX_MONTHLY_INCOME) {
			return `A jövedelem nem lehet több mint ${formatText(MAX_MONTHLY_INCOME, "ft")}!`;
		}
	}

	private validRealEstateValue(value: number) {
		if (value < MIN_REAL_ESTATE_VALUE) {
			return `Nem lehet az ingatlan értéke ${formatText(MIN_REAL_ESTATE_VALUE, "ft")}!`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
